import Component from "@ember/component"
import { action, computed, get, set } from "@ember/object"
import { inject as service } from "@ember/service"
import { isNone } from "@ember/utils"
import type { CardStack } from "@shared/models/card-stack"
import type { Planbook } from "@shared/models/planbook"
import type { PlanbookCalendarDateCombined } from "@shared/models/planbook-calendar-date-combined"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import PLANBOOK_DATE_RESET_TO_TEMPLATE from "cc-frontend/lib/actions/planbook/PLANBOOK_DATE_RESET_TO_TEMPLATE"
import type RoutineEditor from "cc-frontend/services/routine-editor"
import { isNil, sortBy } from "lodash"
import type Dispatcher from "../../../services/dispatcher"

interface RoutineModels {
  planbookDate: PlanbookCalendarDateCombined
  routine: CardStack
  rotationCalendar: RotationCalendar
  planbook: Planbook
  dateString: string
}

export default class RoutineComponent extends Component {
  // Args
  models!: RoutineModels
  canShowAddMenu: boolean = false
  isFilteringClasses!: boolean
  onLessonLoad?: (courseId: string) => void

  // state
  typeOfLessonToShow
  addToRoutineMenuConstraints = [{ to: "window", pin: ["left"] }]
  hasSetAddMenu = false

  // Services
  @service declare dispatcher: Dispatcher
  @service dragging
  @service notifier
  @service declare routineEditor: RoutineEditor

  // init
  mouseEnter() {
    if (get(get(this, "dragging"), "isDragging")) return
  }

  checkForRoutineFirstEdit() {
    if (isNone(get(get(get(this, "models"), "planbookDate"), "cardStackId"))) {
      get(this, "dispatcher").dispatch("ROUTINE_FIRST_EDIT", {
        planbook: get(this.get("models"), "planbook"),
        cardStackId: get(get(get(this, "models"), "routine"), "id"),
        dateString: get(this.get("models"), "dateString"),
        cards: get(get(get(get(this, "models"), "routine"), "attributes"), "cards"),
      })
    }
  }

  // Computed Properties
  @computed("models.routine.attributes.cards", "models.routine.attributes._revision")
  get sortedCards() {
    if (isNil(this.models.routine)) return []
    if (isNil(this.models.routine) || isNil(this.models.routine.attributes)) return undefined
    return sortBy(this.models.routine.attributes.cards, (card) => {
      return card.attributes.position
    })
  }
  // Cps/getters
  // Actions

  @action
  addCourse(id) {
    // @ts-ignore - on courseCreate is not assignable to parameter of type "keyof this"
    this.get("onCourseCreate")(id)
    if (!this.isDestroyed || !this.isDestroying) set(this, "canShowAddMenu", false)
  }

  @action
  toggleAddMenu() {
    set(this, "canShowAddMenu", !get(this, "canShowAddMenu"))
  }

  @action
  reorderRoutineCards(_oldList, newList, draggedItem) {
    this.routineEditor.reorderCards(
      this.models.planbook,
      this.models.routine,
      this.models.dateString,
      newList,
      draggedItem
    )
  }

  @action
  resetToTemplate() {
    this.dispatcher.dispatchAction(PLANBOOK_DATE_RESET_TO_TEMPLATE, {
      planbookId: this.models.planbook.id,
      currentCardStackId: this.models.planbookDate.cardStackId,
      dateString: this.models.dateString,
      dateId: this.models.planbookDate.planbookDateId,
      rotationDayTitle: this.models.planbookDate.rotationDayTitle,
    })
    let formattedDate = dateFns.format(dateFns.parse(this.models.dateString), "MMM, Do, YYYY")

    this.notifier.success("", {
      title: `Great! ${formattedDate} is now using the normal schedule.`,
      message: "Click the Undo Button to undo this.",
      duration: 5000,
    })
  }

  showMoreExplainer = false
  @action
  showUpdatingFromTemplateMore() {
    this.set("showMoreExplainer", true)
  }
}
